{
  "hero": {
    "title": "NAPAICP technical architecture",
    "description": "This page describes the platform's system design and technical architecture, moving from a conceptual overview to implementation detail."
  },
  "quickNav": {
    "label": "On this page",
    "overview": "System overview",
    "diagram": "Request flow",
    "stack": "Tech stack",
    "principles": "Design priorities",
    "security": "Security & data protection"
  },
  "overview": {
    "heading": "System overview",
    "text": "NAPAICP is built on a strict frontend/backend separation. The frontend is responsible only for rendering and user interaction; it makes no authorization decisions of its own. All identity verification, permission checks, and business logic are centralized in the backend service. Every request, whether it arrives through the web interface or calls the API directly, passes through the same verification and authorization path; there is no route that bypasses it.",
    "detail": "The platform's authoritative records (users, competitions, teams, submissions, scores) are stored in a dedicated database; submitted files are stored in a separate object storage service. Both are accessed exclusively through signed URLs issued by the backend, valid for minutes at most; any unauthorized direct access request is rejected."
  },
  "diagram": {
    "heading": "Request flow",
    "description": "A typical action (signing in, viewing a competition, submitting a file) passes through these layers in order:",
    "browser": {
      "title": "Browser",
      "caption": "The web interface a user interacts with"
    },
    "pages": {
      "title": "Frontend",
      "subtitle": "Cloudflare Pages",
      "caption": "Static pages only; makes no authorization decisions"
    },
    "worker": {
      "title": "Backend",
      "subtitle": "Cloudflare Workers",
      "caption": "The sole point where identity and permissions are verified"
    },
    "d1": {
      "title": "Database",
      "subtitle": "D1",
      "caption": "Structured records: users, teams, submissions, scores"
    },
    "r2": {
      "title": "File storage",
      "subtitle": "R2",
      "caption": "Submission files, always private, accessed only via short-lived signed URLs"
    },
    "upload": "When a browser uploads a file, the data goes directly to file storage rather than through the backend host; the backend's role is limited to issuing the time-limited access credential."
  },
  "stack": {
    "heading": "Tech stack",
    "description": "Everything below is running in production, not a proposal under evaluation.",
    "frontend": {
      "title": "Frontend",
      "simple": "The web interface a user operates.",
      "detail": "React 19 + Vite + Tailwind CSS, built into static assets and served from Cloudflare Pages."
    },
    "backend": {
      "title": "Backend",
      "simple": "The service layer handling business logic and authorization.",
      "detail": "Hono (a lightweight web framework) with Zod (type validation), running on Cloudflare Workers, answering requests from the nearest edge location worldwide."
    },
    "database": {
      "title": "Database",
      "simple": "Holds the authoritative record of users, competitions, submissions, and scores.",
      "detail": "Cloudflare D1 (SQLite-based). Every schema change is applied through a version-controlled migration file; the schema is never mutated at startup."
    },
    "storage": {
      "title": "File storage",
      "simple": "Holds participants' submitted files.",
      "detail": "Cloudflare R2. The bucket remains fully private with no public access configured; all reads and writes go through short-lived signed URLs issued by the backend."
    },
    "coordination": {
      "title": "State coordination",
      "simple": "Handles consistency when multiple parties act on the same resource concurrently.",
      "detail": "Cloudflare Durable Objects, used to serialize submission-lock transitions, multipart upload lifecycles, and rate-limit counters."
    },
    "email": {
      "title": "Transactional email",
      "simple": "Sends verification emails, password resets, and advancement notifications.",
      "detail": "Cloudflare Email Service. A delivery failure is logged only; it does not affect the success of the user's original action."
    }
  },
  "principles": {
    "heading": "Design priorities",
    "description": "This platform is used to judge real competitions. When design decisions conflict, they are resolved in this order:",
    "order": [
      "Data integrity: data must never be corrupted or lost for any reason",
      "Authorization: only those authorized may perform the actions they're authorized for",
      "Competition isolation: one competition's data is never accessible to another",
      "Auditability: every change is traceable to who made it, when, and why",
      "Correctness: system behavior must match specification, with no computational errors",
      "Usability: the system must remain operable in practice",
      "Performance: response times should stay within a reasonable range",
      "Visual presentation: the interface should be consistent and usable"
    ],
    "footnote": "In practice: a system that responds more slowly but computes correctly and leaves an auditable trail takes priority over one that responds quickly but may compute incorrectly or leave no record afterward."
  },
  "security": {
    "heading": "Security & data protection",
    "isolation": {
      "title": "Competition data isolation",
      "simple": "Data belonging to one competition is never visible to another.",
      "detail": "Every row belonging to a specific competition (forms, teams, submissions, scores) is bound to that competition's id, and every query must carry it, verified centrally by the backend."
    },
    "anonymous": {
      "title": "Judge anonymity",
      "simple": "While scoring, a judge sees only an anonymous code, never which team or student it belongs to.",
      "detail": "This restriction is enforced at the API response shape itself, not merely hidden in the UI; even a direct API call cannot retrieve identity information."
    },
    "files": {
      "title": "Private file access",
      "simple": "Submission files are never served from a public URL.",
      "detail": "Every file access goes through a signed URL issued by the backend, valid for minutes at most; the storage bucket itself has no public-access configuration at all."
    },
    "audit": {
      "title": "Audit trail",
      "simple": "Sensitive actions (role changes, status changes, score adjustments) always leave a record.",
      "detail": "The audit log is append-only, with no edit or delete path; sensitive fields such as passwords are stripped before anything is ever written."
    }
  },
  "cta": {
    "heading": "Further reading",
    "aboutCta": "About the platform",
    "howtoCta": "How-to guide",
    "signInCta": "Sign in"
  },
  "adminGuide": {
    "heading": "Platform administrator?",
    "description": "See the full NAPAICP Platform Administration Guide, with step-by-step coverage of the platform and competition admin interfaces.",
    "action": "NAPAICP Platform Administration Guide"
  }
}
